/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useState, useEffect } from 'react';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Toolbar,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { differenceInDays, format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardActions } from 'modules/dashboard/slice';
import {
  selectVisualFilters,
  selectPmsSync,
  selectPaceHorizon,
  selectPaceBookingStyle,
  selectBreakdownDetails,
  selectEnableHotelGroups,
  selectTargetDate,
  selectIsSignedIn,
  selectHotelName,
  selectHotelDropdownList,
  selectHotelGroupDropdownList,
  selectMergedHotelList,
  selectsHotelForecast,
  selectPredefinedHotelGroups,
  selectIsDemoModeActivated,
  selectResetBookingDate,
  selectTabList,
} from 'modules/dashboard/selectors';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { getSelectedBreakdown, shortcut } from 'modules/dashboard/functions';
import {
  PACE_HORIZON_SELECTION,
  REPORT_TYPE,
  SHORTCUT_KEY,
  TAGS,
} from 'modules/dashboard/constants';
import { CustomStack } from 'modules/common/components';
import { capitalizeFirst } from 'modules/common/helpers/strings';
import ActionButton from 'modules/common/components/action-button';
import DataUpdatedLabel from 'modules/dashboard/components/tab-container/data-updated-label';
import PmsToggle from 'modules/dashboard/components/tab-container/inner-filter/components/pms-toggle';
import LagAlignedWindow from 'modules/dashboard/components/tab-container/inner-filter/components/lag-aligned-window';
import BreakdownWindow from 'modules/dashboard/components/tab-container/inner-filter/components/breakdown-window';
import {
  getDateComparisonFilter,
  getFocusOnFilter,
  getPercentageList,
  getSubAlignmentFilter,
  getSubBreakdownFilter,
  getSubPaceFilter,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import { ENVIRONMENT } from 'config';
import { isEmpty } from 'modules/common/helpers/object';
import {
  selectActivePaceTab,
  selectEnableSecondaryHotelGroups,
  selectHotelFocusOn,
  selectIsForecast,
  selectIsPaceDetailed,
  selectIsPaceForecastDisabled,
  selectIsPrimary,
  selectNormalizedView,
  selectPaceAlignment,
  selectPaceAlignmentToggle,
  selectPaceBookingDate,
  selectPaceBookingPaceWindow,
  selectPaceChartBreakdown,
  selectPaceCumulativeView,
  selectPaceDateComparison,
  selectPaceFocusOn,
  selectPaceLagAligned,
  selectPaceSymmetricComparisonToggle,
  selectPercentage,
  selectSymmetricComparisonIsDisplay,
  selectIsFocusOnSet,
  selectIsPaceSegmentTimeDeAggregate,
  selectIsPaceSegmentTimeDeAggregateDisabled,
  selectOriginalHotelFocusOn,
  selectIsSecondaryHotelSetOverTimeDisabled,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
  selectIsPaceLeapComparison,
} from '../../selectors';
import { paceActions } from '../../slice';
import {
  BookingDatePicker,
  CompareHotelsWindow,
  PercentageWindow,
  VsComparisonWindow,
} from '../../../inner-filter';
import usePaceCommandPipeline from '../../hooks/use-pace-command-pipeline';
import { selectOrganization } from '../../../../../../common/auth/selectors';
import { COMMON_FOCUS_ON_OPTIONS } from '../../../trend-tabs/constants';
import { DATE_TIME_PATTERN } from '../../../../../../common/constants/date-range';

/**
 * Filters for pace widget include PMS Sync, Pace Horizon, pace variable and pace forecast
 * @param {Number} tabValue - Pace widget tab value
 * @param {Number} ownTabValue - Tab value of the tab this filter panel belongs to
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {Boolean} hotelTimezone - Timezone of selected hotel
 * @param {Number} toggleDisabled - PMS sync toggle disabled state
 * @param {Boolean} isLoaded - Visual is loaded state
 * @param {String} reportType - Report type of active tab
 * @returns
 */
const PaceFilter = ({
  tabValue,
  ownTabValue,
  latestDate,
  hotelTimezone,
  toggleDisabled,
  reportType,
  visual,
  visualName,
  isLoaded,
}) => {
  const dispatch = useDispatch();

  // Selected filter values are obtained from redux store. Refer each selector function for details.
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const paceHorizon = useSelector(selectPaceHorizon);
  const mergedHotelList = useSelector(selectMergedHotelList);
  const customBooking = useSelector(selectPaceBookingStyle);
  const focusOn = useSelector(selectPaceFocusOn);
  const chartBreakdown = useSelector(selectPaceChartBreakdown);
  const breakdownData = useSelector(selectBreakdownDetails);
  const isDetailed = useSelector(selectIsPaceDetailed);
  const paceLagAligned = useSelector(selectPaceLagAligned);
  const paceCumulative = useSelector(selectPaceCumulativeView);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const alignment = useSelector(selectPaceAlignment);
  const isPaceForecast = useSelector(selectIsForecast);
  const hotelList = useSelector(selectHotelDropdownList);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const hotelGroupList = useSelector(selectHotelGroupDropdownList);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  const bookingDate = useSelector(selectPaceBookingDate);
  const isForecastDisabled = useSelector(selectIsPaceForecastDisabled);
  const activeTab = useSelector(selectActivePaceTab);
  const isHotelForecast = useSelector(selectsHotelForecast);
  const organization = useSelector(selectOrganization);
  const predefinedHotelGroups = useSelector(selectPredefinedHotelGroups);
  const selectedHotel = useSelector(selectHotelName);
  const isPaceSegmentTimeDeAggregate = useSelector(selectIsPaceSegmentTimeDeAggregate);
  const isPaceSegmentTimeDeAggregateDisabled = useSelector(
    selectIsPaceSegmentTimeDeAggregateDisabled
  );
  const originalHotelFocusOn = useSelector(selectOriginalHotelFocusOn);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const isHotelSetOverTimeDisabled = useSelector(selectIsSecondaryHotelSetOverTimeDisabled);
  const hotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidSecondaryHotelList);
  const resetBookingDate = useSelector(selectResetBookingDate);
  // Get persisted values for hotel name and stay date
  const serializedState = JSON.parse(localStorage.getItem(`persist:${ENVIRONMENT.PERSIST_KEY}`));
  const targetDate = useSelector(selectTargetDate);
  const isSignIn = useSelector(selectIsSignedIn);
  const percentage = useSelector(selectPercentage);
  const paceAlignmentToggle = useSelector(selectPaceAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectPaceSymmetricComparisonToggle);
  const dateComparison = useSelector(selectPaceDateComparison);
  const symmetricComparisonIsDisplay = useSelector(selectSymmetricComparisonIsDisplay);
  const isPrimary = useSelector(selectIsPrimary);
  const bookingPaceWindow = useSelector(selectPaceBookingPaceWindow);
  const isFocusOnSet = useSelector(selectIsFocusOnSet);
  const hasLeap = useSelector(selectIsPaceLeapComparison);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [paceList, setPaceList] = useState([]);
  const [selectedBreakdown, setSelectedBreakdown] = useState();
  const [chartBreakdownList, setChartBreakdownList] = useState([]);
  const [focusOnList, setFocusOnList] = useState([]);
  // breakdown that the current focus on list was built for
  const [focusOnListBreakdown, setFocusOnListBreakdown] = useState();
  const [percentageList, setPercentage] = useState([]);
  const [isPercentageDisabled, setIsPercentageDisabled] = useState();
  const [dateComparisonList, setDateComparisonList] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [previousFocusOnSelection, setPreviousFocusOnSelection] = useState([]);
  //
  useEffect(() => {
    if (focusOn?.length > 0 && !isFocusOnSet) {
      dispatch(paceActions.setIsFocusOnSet(true));
    }
  }, [focusOn, isFocusOnSet]);
  // check whether all focus on options are selected or not
  useEffect(() => {
    if (focusOnList?.length > 0) {
      const allFocusOnOptionsSelected = focusOnList.length - 1 === focusOn.length;
      setAllSelected(allFocusOnOptionsSelected);
    }
  }, [focusOn, focusOnList]);
  // Triggered on visualFilters, chartBreakdown or isDetails toggle change
  useEffect(() => {
    // Assign values to selected breakdown
    const breakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
    setSelectedBreakdown(breakdown);
  }, [isDetailed, chartBreakdown, visualFilters]);

  useEffect(() => {
    // To off forecast when focus on value is selected
    if (!isEmpty(focusOn)) dispatch(paceActions.setIsPaceForecast(false));
  }, [focusOn]);

  // Reset button function to set filter panel values to default
  const clearSearch = () => {
    dispatch(paceActions.setNormalizedView(false));
    dispatch(paceActions.setIsPrimary(false));
    dispatch(paceActions.setPaceAlignmentToggle(false));
    dispatch(paceActions.setPaceSymmetricComparisonToggle(true));
    dispatch(
      paceActions.setPaceDateComparison(
        dateComparisonList?.filter((filter) => filter?.isDefault === true)[0].value
      )
    );
    dispatch(
      dashboardActions.setPaceHorizon(
        visualFilters?.filter(
          (filter) => filter?.tags?.includes(TAGS.PACE_HORIZON) && filter?.isDefault === true
        )[0]?.id
      )
    );
    dispatch(dashboardActions.setPmsSync(false));
    dispatch(
      paceActions.setPaceChartBreakdown(
        visualFilters?.filter(
          (filter) => filter?.tags?.includes(TAGS.SEGMENT_BREAKDOWN) && filter?.isDefault === true
        )[0].id
      )
    );
    dispatch(paceActions.setIsPaceDetailed(false));
    dispatch(paceActions.setPaceLagAligned(false));
    dispatch(paceActions.setPaceCumulativeView(true));
    dispatch(paceActions.setIsPaceForecast(false));
    dispatch(paceActions.setPaceBookingPaceWindow(PACE_HORIZON_SELECTION.BOOKING_PACE_HORIZON));
    dispatch(paceActions.setPaceBookingDate(resetBookingDate));
    const allOptions = focusOnList.filter(
      (option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
    );
    dispatch(paceActions.setPaceFocusOn(allOptions));
    setAllSelected(true);
    dispatch(
      paceActions.setIsForecastDisabled(
        (!organization?.enableForecastFilter || !isHotelForecast) ?? false
      )
    );
    setPreviousFocusOnSelection(
      chartBreakdownList.reduce((acc, breakdown) => {
        acc[breakdown.id] = { detailedOn: [], detailedOff: [] };
        return acc;
      }, {})
    );
  };

  // Triggered when visualFilter value changes
  useEffect(() => {
    // Update pace variable filter list
    const subPaceFilter = getSubPaceFilter(visualFilters);
    if (subPaceFilter) {
      setPaceList(subPaceFilter);
    }
    // Set chart breakdown list
    const subChartBreakdownList = getSubBreakdownFilter(visualFilters, TAGS.PACE);
    if (subChartBreakdownList) {
      setChartBreakdownList(subChartBreakdownList);
      setPreviousFocusOnSelection(
        subChartBreakdownList.reduce((acc, breakdown) => {
          acc[breakdown.id] = { detailedOn: [], detailedOff: [] };
          return acc;
        }, {})
      );
    }
    // Set percentage list
    const percentageItemList = getPercentageList(visualFilters);
    if (percentageList) {
      setPercentage(percentageItemList);
    }
  }, [visualFilters]);

  // Shortcut function to set pms sync value
  shortcut(tabValue, TAB_VALUE.PACE, SHORTCUT_KEY.PMS_TOGGLE, () =>
    dispatch(dashboardActions.setPmsSync(!pmsSyncToggle))
  );
  // Triggered on change of chartBreakdown, visualFilters, breakdownData or selectedBreakdown
  useEffect(() => {
    // Set focus on list relevant for selected chart breakdown
    if (selectedBreakdown) {
      const focusOnFilter = getFocusOnFilter(breakdownData, selectedBreakdown);
      setFocusOnList(focusOnFilter);
      setFocusOnListBreakdown(selectedBreakdown);
      if (isEmpty(previousFocusOnSelection)) {
        dispatch(
          paceActions.setPaceFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      }
      if (
        isDetailed &&
        !isEmpty(previousFocusOnSelection) &&
        isEmpty(previousFocusOnSelection[chartBreakdown].detailedOn)
      ) {
        dispatch(
          paceActions.setPaceFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      } else if (
        !isDetailed &&
        !isEmpty(previousFocusOnSelection) &&
        isEmpty(previousFocusOnSelection[chartBreakdown].detailedOff)
      ) {
        dispatch(
          paceActions.setPaceFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      }
    }
  }, [visualFilters, breakdownData, selectedBreakdown]);
  //
  useEffect(() => {
    if (
      isDetailed &&
      !isEmpty(previousFocusOnSelection) &&
      previousFocusOnSelection[chartBreakdown] &&
      !isEmpty(previousFocusOnSelection[chartBreakdown].detailedOn)
    ) {
      dispatch(paceActions.setPaceFocusOn(previousFocusOnSelection[chartBreakdown].detailedOn));
    } else if (
      !isDetailed &&
      !isEmpty(previousFocusOnSelection) &&
      !isEmpty(previousFocusOnSelection[chartBreakdown].detailedOff)
    ) {
      dispatch(paceActions.setPaceFocusOn(previousFocusOnSelection[chartBreakdown].detailedOff));
    }
  }, [isPrimary]);
  //
  const focusOnLabel = focusOn?.length ? 'Focused on' : 'Select Focus on';
  /**
   * set pace widget pace horizon value
   * @param {*} event - on change event
   */
  const handlePaceHorizonChange = (event) => {
    dispatch(dashboardActions.setPaceHorizon(event.target.value));
  };
  /**
   * set breakdown value
   * @param {*} event - onChanged event
   */
  const handleBreakdownChange = (event) => {
    if (previousFocusOnSelection[chartBreakdown]) {
      if (isDetailed) {
        previousFocusOnSelection[chartBreakdown].detailedOn = focusOn;
      } else {
        previousFocusOnSelection[chartBreakdown].detailedOff = focusOn;
      }
    }
    dispatch(paceActions.setPaceChartBreakdown(event.target.value));
    dispatch(
      paceActions.setPaceFocusOn(
        isDetailed
          ? previousFocusOnSelection[event.target.value]?.detailedOn
          : previousFocusOnSelection[event.target.value]?.detailedOff
      )
    );
  };
  /**
   * set isDetailed value
   * @param {*} event - onChanged event
   */
  const handleIsDetailedChange = (event) => {
    dispatch(paceActions.setIsPaceDetailed(event.target.checked));
    if (previousFocusOnSelection[chartBreakdown]) {
      if (event.target.checked) {
        previousFocusOnSelection[chartBreakdown].detailedOff = focusOn;
      } else {
        previousFocusOnSelection[chartBreakdown].detailedOn = focusOn;
      }
    }
    if (
      event.target.checked &&
      !isEmpty(previousFocusOnSelection) &&
      previousFocusOnSelection[chartBreakdown] &&
      !isEmpty(previousFocusOnSelection[chartBreakdown].detailedOn)
    ) {
      dispatch(paceActions.setPaceFocusOn(previousFocusOnSelection[chartBreakdown].detailedOn));
    } else if (
      !event.target.checked &&
      !isEmpty(previousFocusOnSelection) &&
      !isEmpty(previousFocusOnSelection[chartBreakdown].detailedOff)
    ) {
      dispatch(paceActions.setPaceFocusOn(previousFocusOnSelection[chartBreakdown].detailedOff));
    }
  };
  // reset focus on drop down when hotel groups option is selected
  useEffect(() => {
    const subBreakdownFilter = getSubBreakdownFilter(visualFilters);
    setPreviousFocusOnSelection(
      !isEmpty(subBreakdownFilter) &&
        subBreakdownFilter?.reduce((acc, breakdown) => {
          acc[breakdown.id] = { detailedOn: [], detailedOff: [] };
          return acc;
        }, {})
    );
  }, [selectedHotel, enableHotelGroups]);
  // Alignment toggle change handler
  const handleToggleChange = (event) => {
    dispatch(paceActions.setPaceAlignmentToggle(event.target.checked));
    const dateComparisonFilter = getDateComparisonFilter(visualFilters, alignment?.id);
    dispatch(
      paceActions.setPaceDateComparison(
        dateComparisonFilter?.filter((filter) => filter?.isDefault === true)[0].value
      )
    );
  };
  /**
   * set percentage value
   * @param {*} event - on changed event
   */
  const handlePercentageChange = (event) => {
    dispatch(paceActions.setPercentage(event.target.value));
  };
  // Triggered when targetDate, latestDate, paceCumulative value change
  useEffect(() => {
    // Set state to enable/ disable percentage filter radio group
    const lastBookingDateUtc = latestDate
      ? DateTime.fromSQL(format(new Date(latestDate), DATE_TIME_PATTERN), { zone: 'utc' })
      : '';
    const formattedLastBookingDate = latestDate
      ? lastBookingDateUtc.setZone(hotelTimezone).toFormat(DATE_TIME_PATTERN)
      : '';
    const targetDateUtc = targetDate.endDate
      ? DateTime.fromSQL(format(new Date(targetDate.endDate), DATE_TIME_PATTERN), {
          zone: 'utc',
        })
      : '';
    if (paceCumulative && formattedLastBookingDate && !isPrimary) {
      setIsPercentageDisabled(
        !(differenceInDays(new Date(targetDateUtc), new Date(formattedLastBookingDate)) <= 0)
      );
    } else {
      setIsPercentageDisabled(true);
    }
  }, [targetDate, latestDate, paceCumulative, isPrimary]);
  /**
   * set symmetric comparison value
   * @param {*} event - on change event
   */
  const handleSymmetricComparisonChange = (event) => {
    dispatch(paceActions.setPaceSymmetricComparisonToggle(event.target.checked));
  };
  /**
   * set comparison window value
   * @param {*} event - on changed event
   */
  const handleComparisonChange = (event) => {
    dispatch(paceActions.setPaceDateComparison(event.target.value));
  };
  // Triggered when alignment, visualFilters, alignmentToggle changes
  useEffect(() => {
    // Set date comparison list based on alignment
    if (alignment) {
      const dateComparisonFilter = getDateComparisonFilter(visualFilters, alignment?.id);
      setDateComparisonList(dateComparisonFilter);
      if (!isSignIn && serializedState) {
        return;
      }
      dispatch(
        paceActions.setPaceDateComparison(
          dateComparisonFilter?.filter((filter) => filter?.isDefault === true)[0].value
        )
      );
    }
  }, [alignment, visualFilters, paceAlignmentToggle]);
  // Triggered when alignmentToggle, visualFilters values change
  useEffect(() => {
    // Set alignment list
    const subAlignmentFilter = getSubAlignmentFilter(visualFilters, paceAlignmentToggle);
    dispatch(
      paceActions.setPaceAlignment({
        id: subAlignmentFilter?.id,
        label: capitalizeFirst(subAlignmentFilter?.type),
      })
    );
  }, [paceAlignmentToggle, visualFilters]);
  useEffect(() => {
    if (paceLagAligned) {
      dispatch(paceActions.setPaceBookingPaceWindow(PACE_HORIZON_SELECTION.BOOKING_PACE_HORIZON));
    }
  }, [paceLagAligned]);
  //
  useEffect(() => {
    if (bookingPaceWindow === PACE_HORIZON_SELECTION.BOOKING_PACE_HORIZON) {
      dispatch(
        paceActions.setPaceBookingDate({
          ...bookingDate,
          endDate: targetDate.endDate,
        })
      );
    }
  }, [bookingPaceWindow, targetDate]);
  //
  // focus on list is rebuilt after a breakdown change, until then it holds previous breakdown options
  const currentBreakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
  const isFocusOnListReady = !currentBreakdown || focusOnListBreakdown === currentBreakdown;
  // pace filter command flow implementation
  usePaceCommandPipeline(tabValue, ownTabValue, {
    toggleDisabled,
    isLoaded,
    chartBreakdownList,
    focusOnList,
    isFocusOnListReady,
    paceList,
    percentageList,
    visualFilters,
    customBooking,
    dateComparison,
    hotelList,
    hotelGroupList: [...hotelGroupList, ...predefinedHotelGroups],
    handleComparisonChange,
    handleToggleChange,
  });
  //
  useEffect(() => {
    // Assign forecast disabled state on breakdown focus on change and active tab change
    if (activeTab === REPORT_TYPE.PACE_SEGMENT_WIDGET || !allSelected) {
      dispatch(paceActions.setIsForecastDisabled(true));
      dispatch(paceActions.setIsPaceForecast(false));
    } else {
      dispatch(
        paceActions.setIsForecastDisabled(
          (!organization?.enableForecastFilter || !isHotelForecast) ?? false
        )
      );
    }
  }, [activeTab, focusOn, isHotelForecast, allSelected]);
  //
  useEffect(() => {
    if (isPrimary) {
      dispatch(paceActions.setIsPaceSegmentTimeDeAggregate(true));
      dispatch(paceActions.setIsPaceSegmentTimeDeAggregateDisabled(false));
    } else {
      dispatch(paceActions.setIsPaceSegmentTimeDeAggregate(false));
      dispatch(paceActions.setIsPaceSegmentTimeDeAggregateDisabled(true));
    }
  }, [isPrimary]);
  //
  return (
    <>
      <PmsToggle
        disabled={toggleDisabled}
        value={pmsSyncToggle}
        onChange={(event) => dispatch(dashboardActions.setPmsSync(event.target.checked))}
        id="pace-pms-toggle"
      />{' '}
      <br />
      <CustomStack cssClassName={styles.innerFilter}>
        <FormControl>
          <Typography sx={{ color: '#0009' }}>Booking Window</Typography>
          <RadioGroup
            value={bookingPaceWindow ?? PACE_HORIZON_SELECTION.BOOKING_PACE_HORIZON}
            name="radio-buttons-group"
            onChange={(event) => {
              dispatch(paceActions.setPaceBookingPaceWindow(event.target.value));
              dispatch(paceActions.setPaceBookingDate(resetBookingDate));
            }}
          >
            <FormControlLabel
              id="auto-toggle-booking-pace-window"
              sx={{
                my: -1,
              }}
              value={PACE_HORIZON_SELECTION.BOOKING_PACE_WINDOW}
              control={<Radio />}
              label="Custom"
              disabled={paceLagAligned}
              className="booking-pace-window"
            />
            {bookingPaceWindow === PACE_HORIZON_SELECTION.BOOKING_PACE_WINDOW && (
              <BookingDatePicker
                id="auto-pace-booking-date-picker"
                reportType={reportType}
                latestDate={latestDate}
              />
            )}
            <FormControlLabel
              id="auto-toggle-booking-pace-horizon"
              sx={{
                my: -1,
              }}
              value={PACE_HORIZON_SELECTION.BOOKING_PACE_HORIZON}
              control={<Radio />}
              label="Booking Pace Horizon"
              className="booking-pace-horizon"
            />

            <RadioGroup value={paceHorizon ?? null} onChange={handlePaceHorizonChange}>
              {paceList
                ?.sort(
                  (element1, element2) =>
                    element1.label?.[0] &&
                    element2.label?.[0] &&
                    Number(element1?.label?.[0]?.split(' ')[0]) -
                      Number(element2?.label?.[0]?.split(' ')[0])
                )
                ?.map((item) => (
                  <FormControlLabel
                    id={`${item?.label?.[0]?.split(' ')[0]}-pace-horizon`}
                    key={item?.id}
                    value={item?.id}
                    control={<Radio />}
                    label={item?.label}
                    disabled={bookingPaceWindow === PACE_HORIZON_SELECTION.BOOKING_PACE_WINDOW}
                    sx={{
                      ml: 2,
                      my: -1,
                    }}
                  />
                ))}
            </RadioGroup>
          </RadioGroup>
        </FormControl>
      </CustomStack>
      <br />
      <LagAlignedWindow
        paceLagAligned={paceLagAligned}
        paceCumulative={paceCumulative}
        changePaceLagAligned={paceActions.setPaceLagAligned}
        changePaceCumulative={paceActions.setPaceCumulativeView}
        isPaceForecast={isPaceForecast}
        IsForecastDisabled={isForecastDisabled}
        setIsPaceForecast={paceActions.setIsPaceForecast}
      />
      <br />
      <BreakdownWindow
        id="pace"
        isDetailed={isDetailed}
        chartBreakdown={chartBreakdown}
        chartBreakdownList={chartBreakdownList}
        handleIsDetailedChange={handleIsDetailedChange}
        handleBreakdownChange={handleBreakdownChange}
        isFocusOnAvailable
        focusOnList={focusOnList}
        focusOn={focusOn}
        focusOnLabel={focusOnLabel}
        handleFocusOnChange={(event, value, reason) => {
          if (reason === 'selectOption' || reason === 'removeOption') {
            if (
              value.find((option) => option.label === COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label) &&
              value?.filter((option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label)
                .length === focusOn.length &&
              value?.length === focusOnList.length
            ) {
              const allOptions = [];
              dispatch(paceActions.setPaceFocusOn(allOptions));
              if (previousFocusOnSelection[chartBreakdown]) {
                if (isDetailed) {
                  previousFocusOnSelection[chartBreakdown].detailedOn = allOptions;
                } else {
                  previousFocusOnSelection[chartBreakdown].detailedOff = allOptions;
                }
              }

              setAllSelected(false);
            } else if (
              value.find((option) => option.label === COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label)
            ) {
              // Select all options if "Select All" is not selected
              const allOptions = focusOnList.filter(
                (option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
              );
              dispatch(paceActions.setPaceFocusOn(allOptions));
              if (previousFocusOnSelection[chartBreakdown]) {
                if (isDetailed) {
                  previousFocusOnSelection[chartBreakdown].detailedOn = allOptions;
                } else {
                  previousFocusOnSelection[chartBreakdown].detailedOff = allOptions;
                }
              }
              setAllSelected(true);
            } else {
              dispatch(paceActions.setPaceFocusOn(value));
              if (previousFocusOnSelection[chartBreakdown]) {
                if (isDetailed) {
                  previousFocusOnSelection[chartBreakdown].detailedOn = value;
                } else {
                  previousFocusOnSelection[chartBreakdown].detailedOff = value;
                }
              }
              setAllSelected(
                value?.filter((option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label)
                  .length ===
                  focusOnList?.filter(
                    (option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
                  ).length
              );
            }
          }
        }}
        allSelected={allSelected}
        isEnableBreakdownCheckbox
        handlePrimaryChange={(event) => dispatch(paceActions.setIsPrimary(event.target.checked))}
        isPrimary={isPrimary}
        isDeAggregateToggle
        isTrendDeAggregate={isPaceSegmentTimeDeAggregate}
        isTrendDeAggregateDisabled={isPaceSegmentTimeDeAggregateDisabled}
        handleIsDeAggregateToggleChange={(event) =>
          dispatch(paceActions.setIsPaceSegmentTimeDeAggregate(event.target.checked))
        }
      />
      <br />
      <CompareHotelsWindow
        selectedHotelName={selectHotelName}
        hotelList={hotelList ?? []}
        mergedHotelList={mergedHotelList ?? []}
        focusOn={hotelFocusOn}
        focusOnLabel={hotelFocusOn?.length ? 'Compared with' : 'Select Hotel'}
        handleCompareHotelsChange={(event, value) => {
          dispatch(paceActions.setHotelFocusOn(value));
          dispatch(paceActions.setIsSecondaryHotelSetOverTime(false));
        }}
        normalizedViewToggle={normalizedViewToggle}
        handleTrendHotelNormalize={(event) =>
          dispatch(paceActions.setNormalizedView(event.target.checked))
        }
        enableSecondaryHotelGroups={enableSecondaryHotelGroups}
        hotelGroupList={hotelGroupList}
        originalHotelFocusOn={originalHotelFocusOn}
        isDemoModeActivated={isDemoModeActivated}
        setOriginalHotelFocusOn={(focus) => {
          dispatch(paceActions.setOriginalHotelFocusOn(focus));
        }}
        resetFocusOn={(focus) => {
          dispatch(paceActions.resetHotelFocusOn(focus));
        }}
        hotelSetOverTime={hotelSetOverTime}
        handleHotelSetChange={(event) => {
          dispatch(paceActions.setIsSecondaryHotelSetOverTime(event.target.checked));
          if (hotelFocusOn?.id === selectedHotel?.id) {
            dispatch(dashboardActions.setIsPrimaryHotelSetOverTime(event.target.checked));
          }
        }}
        isHotelSetOverTimeDisabled={isHotelSetOverTimeDisabled}
        invalidPrimaryHotels={invalidPrimaryHotels}
      />
      <br />
      <VsComparisonWindow
        id="pace"
        customBooking={customBooking}
        alignmentToggle={paceAlignmentToggle}
        handleToggleChange={handleToggleChange}
        symmetricComparisonToggle={symmetricComparisonToggle}
        visual={visual}
        visualName={visualName}
        dateComparison={dateComparison}
        dateComparisonList={dateComparisonList}
        handleComparisonChange={handleComparisonChange}
        handleSymmetricComparisonChange={handleSymmetricComparisonChange}
        isCustomOptionAvailable={false}
        hideOTBAndCustom
        reportType={reportType}
        isDisplay={symmetricComparisonIsDisplay}
        isDisplayOnClickAction={() => {
          dispatch(paceActions.setSymmetricComparisonIsDisplay(!symmetricComparisonIsDisplay));
        }}
        showStlp={false}
        hasLeap={hasLeap}
      />
      <br />
      <PercentageWindow
        percentage={percentage}
        percentageList={percentageList}
        handlePercentageChange={handlePercentageChange}
        isPercentageDisabled={isPercentageDisabled}
      />
      <CustomStack cssClassName={styles.innerFilter}>
        <ActionButton onClick={clearSearch} label="RESET" />
      </CustomStack>
      <Toolbar />
      <DataUpdatedLabel
        latestDate={latestDate}
        hotelTimezone={hotelTimezone}
        toggleDisabled={toggleDisabled}
      />
    </>
  );
};
//
export default PaceFilter;
